/* Universal javascript */

import Alpine from 'alpinejs';
import persist from '@alpinejs/persist';

import './alpine/store.js';
import './alpine/popovers.js';
import './alpine/skeleton.js';
import './alpine/whereAutocomplete.js';
import './alpine/searchComposer.js';
// import './components/heroSearch.js';
import './components/bookingFlow.js';
import listingBooking from './alpine/listingBooking.js';
import { priceRange } from './components/priceRange.js';
import { inlineBirdhouseAnimation } from './components/inlineBirdhouseAnimation.js';
import { asideAnimation } from './components/asideAnimation.js';
import reviewsCarousel from './components/reviewsCarousel.js';
import stepTimeline from './components/stepTimeline.js';
import listingLocalGuide from './components/listingLocalGuide.js';
import listingWeather from './components/listingWeather.js';

import disableTransitionOnResize from './utils/disableTransitionOnResize.js';
import swiper from './utils/swiper.js';
import animateIn from './utils/animateIn.js';
import htmxEvents from './utils/htmx.js';
import refreshCsrfToken from './utils/refresh-csrf-token.js';
import { initFreshCsrfFormSubmissions } from './utils/csrfRequest.js';
import { initAttribution } from './utils/attribution.js';
import { initHubSpotAttribution } from './utils/hubspotAttribution.js';
import { initLazyRevenueProjectionForm } from './utils/lazyRevenueProjectionForm.js';
import { initEmailDomainSuggestions } from './utils/emailDomainSuggestion.js';
import {
    initAuthListeners,
    initCardClickTracking,
    initViewItem
} from './utils/dataLayer.js';
import recentBookingCue from './components/recentBookingCue.js';

initAttribution();
initHubSpotAttribution();
initLazyRevenueProjectionForm();

async function registerPageAlpineComponents() {
    const imports = [];

    if (document.querySelector('[x-data^="propertyMap"]')) {
        imports.push({
            name: 'propertyMap',
            promise: import('./alpine/propertyMap.js')
        });
    }
    if (document.querySelector('[x-data^="locationMap"]')) {
        imports.push({
            name: 'locationMap',
            promise: import('./alpine/locationMap.js')
        });
    }
    if (document.querySelector('[x-data^="searchMap"]')) {
        imports.push({
            name: 'searchMap',
            promise: import('./alpine/searchMap.js')
        });
    }

    const results = await Promise.allSettled(
        imports.map((component) => component.promise)
    );
    results.forEach((result, index) => {
        if (result.status === 'rejected') {
            console.error(
                `Unable to load optional Alpine component "${imports[index].name}".`,
                result.reason
            );
        }
    });
}

function initializeDocument() {
    disableTransitionOnResize.init();
    listingLocalGuide.init();
    listingWeather.init();
    swiper.init();
    inlineBirdhouseAnimation.init();
    asideAnimation.init();
    reviewsCarousel.init();
    stepTimeline.init();
    animateIn.init();
    priceRange.autoInit();
    priceRange.watchDialogs();

    htmxEvents.init();
    initFreshCsrfFormSubmissions();
    initAuthListeners();
    initCardClickTracking();
    initViewItem();
    initEmailDomainSuggestions();
    recentBookingCue.init();

    // Keep window.__csrfToken available for legacy custom JS requests, but only
    // after an intentional refresh. Do not fetch CSRF on page load; that can
    // race with Sprig forms that already rendered their own tokens.
    document.addEventListener('csrf:refreshed', (e) => {
        window.__csrfToken = e.detail;
    });

    if (window.htmx) {
        const refreshSharedCsrfToken = () => {
            refreshCsrfToken.getTokenAsync(true);
        };

        window.htmx.on('loginSucceeded', refreshSharedCsrfToken);
        window.htmx.on('signupSucceeded', refreshSharedCsrfToken);
    }
}

async function bootstrap() {
    await registerPageAlpineComponents();

    // Register Alpine components
    Alpine.data('listingBooking', listingBooking);

    window.Alpine = Alpine;
    Alpine.plugin(persist);
    Alpine.start();

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeDocument, {
            once: true
        });
    } else {
        initializeDocument();
    }
}

bootstrap();
